import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useReveal } from "../hooks/useReveal";
import { useCountUp } from "../hooks/useCountUp";
import { PublicNav } from "../components/public/PublicNav";
import { PublicFooter } from "../components/public/PublicFooter";
import { GalaEnterpriseCallout } from "../components/public/GalaEnterpriseCallout";
import {
  PUBLIC_PRICING_PLANS,
  landingPlanBullets,
  maxOnlineOrdersIncluded,
  type SubscriptionPlanCatalogEntry,
} from "../lib/subscription-plans";

/* ── reveal wrapper ──────────────────────────────────────────────────── */

function Reveal({
  children,
  className = "",
  delay = 0,
}: {
  children: React.ReactNode;
  className?: string;
  delay?: number;
}) {
  const { ref, visible } = useReveal();
  return (
    <div
      ref={ref}
      className={`reveal ${visible ? "visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

/* ── rotating chat phone ─────────────────────────────────────────────── */

type Bubble = { from: "customer" | "bot"; text: string };

const SCENARIOS: { id: string; label: string; bubbles: Bubble[] }[] = [
  {
    id: "order",
    label: "Placing an Order",
    bubbles: [
      { from: "customer", text: "Hi! I'd like a Zinger Burger 🍔" },
      { from: "bot", text: "Great choice! Zinger Burger = PKR 450. Want Large Fries for PKR 150?" },
      { from: "customer", text: "Yes please!" },
      { from: "bot", text: "Total: PKR 600. Delivery or pickup?" },
      { from: "customer", text: "Delivery to Johar Town" },
      { from: "bot", text: "✅ Order confirmed! Estimated arrival: 35 mins." },
    ],
  },
  {
    id: "stock",
    label: "Checking Availability",
    bubbles: [
      { from: "customer", text: "Is Special Biryani available? 🍛" },
      { from: "bot", text: "Yes! Full Tray (PKR 1,200) or Half Tray (PKR 700)?" },
      { from: "customer", text: "Full tray, delivery to DHA Phase 5" },
      { from: "bot", text: "PKR 1,300 incl. delivery. Name for the order?" },
      { from: "customer", text: "Ahmed, +92 300 1234567" },
      { from: "bot", text: "✅ Placed! Arriving in ~50 mins 🎉" },
    ],
  },
  {
    id: "hours",
    label: "Checking Opening Hours",
    bubbles: [
      { from: "customer", text: "What time do you close tonight? 🕐" },
      { from: "bot", text: "We're open until midnight tonight. Plenty of time!" },
      { from: "customer", text: "Great — give me a half Chicken Karahi" },
      { from: "bot", text: "Half Karahi = PKR 850. Dine-in or delivery?" },
      { from: "customer", text: "Dine-in, table for 2 please" },
      { from: "bot", text: "✅ Table for 2 reserved! See you soon 🎉" },
    ],
  },
  {
    id: "modify",
    label: "Modifying an Order",
    bubbles: [
      { from: "customer", text: "Can I change my order? I want to add a Pepsi" },
      { from: "bot", text: "Sure! Pepsi (PKR 80) added. Updated total: PKR 680. Confirm?" },
      { from: "customer", text: "Actually make it 2 Pepsis" },
      { from: "bot", text: "2 × Pepsi (PKR 160). New total: PKR 760. Confirm?" },
      { from: "customer", text: "Yes, confirmed!" },
      { from: "bot", text: "✅ Order updated! Delivery in ~30 mins." },
    ],
  },
  {
    id: "catering",
    label: "Catering Enquiry",
    bubbles: [
      { from: "customer", text: "Do you do catering for 30 people? 🍽️" },
      { from: "bot", text: "Absolutely! We have catering packages. Event date?" },
      { from: "customer", text: "This Saturday, lunch for 30 people" },
      { from: "bot", text: "Mixed BBQ platter for 30 = PKR 18,000. Want to add desserts?" },
      { from: "customer", text: "Yes, add gulab jamun for everyone" },
      { from: "bot", text: "✅ Catering confirmed! Total PKR 21,000. We'll call to confirm details." },
    ],
  },
];

const PHONE_INPUT_CHAR_MS = 32;
const PHONE_PAUSE_BEFORE_SEND_MS = 420;
const PHONE_SEND_ANIM_MS = 260;
const PHONE_PAUSE_AFTER_SCENARIO_MS = 3000;
const PHONE_FADE_MS = 500;

function phoneBotTypingDelayMs(text: string): number {
  const base = 1300;
  const perChar = 30;
  const cap = 4200;
  return Math.min(cap, Math.max(base, text.length * perChar)) + 380;
}

function PhoneMockup() {
  const [scenarioIdx, setScenarioIdx] = useState(0);
  const [visibleCount, setVisibleCount] = useState(0);
  const [isBotTyping, setIsBotTyping] = useState(false);
  const [inputDraft, setInputDraft] = useState("");
  const [isComposing, setIsComposing] = useState(false);
  const [sendPulse, setSendPulse] = useState(false);
  const [fading, setFading] = useState(false);
  const [paused, setPaused] = useState(false);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const bottomRef = useRef<HTMLDivElement>(null);

  const scenario = SCENARIOS[scenarioIdx];
  const finished =
    visibleCount >= scenario.bubbles.length && !isBotTyping && !isComposing;

  /* Scroll WITHIN the chat container only — never touches the page scroll */
  useEffect(() => {
    const container = chatContainerRef.current;
    const bottom = bottomRef.current;
    if (!container || !bottom) return;
    const top = bottom.offsetTop - container.clientHeight + bottom.clientHeight;
    container.scrollTo({ top, behavior: "smooth" });
  }, [visibleCount, isBotTyping, inputDraft, scenarioIdx]);

  /* playback: bot typing OR customer types in input → send */
  useEffect(() => {
    if (paused || fading) return;

    const all = scenario.bubbles;
    if (visibleCount >= all.length) {
      setIsBotTyping(false);
      setIsComposing(false);
      setInputDraft("");
      return;
    }

    const next = all[visibleCount];
    const timers: number[] = [];
    let intervalId: number | undefined;

    if (next.from === "bot") {
      setInputDraft("");
      setIsComposing(false);
      setSendPulse(false);
      setIsBotTyping(true);

      const t = window.setTimeout(() => {
        setIsBotTyping(false);
        setVisibleCount((c) => c + 1);
      }, phoneBotTypingDelayMs(next.text));
      timers.push(t);
    } else {
      setIsBotTyping(false);
      setIsComposing(true);
      setInputDraft("");

      const text = next.text;
      let i = 0;

      intervalId = window.setInterval(() => {
        i += 1;
        setInputDraft(text.slice(0, i));

        if (i >= text.length) {
          if (intervalId !== undefined) window.clearInterval(intervalId);

          const t1 = window.setTimeout(() => {
            setSendPulse(true);

            const t2 = window.setTimeout(() => {
              setSendPulse(false);
              setIsComposing(false);
              setInputDraft("");
              setVisibleCount((c) => c + 1);
            }, PHONE_SEND_ANIM_MS);
            timers.push(t2);
          }, PHONE_PAUSE_BEFORE_SEND_MS);
          timers.push(t1);
        }
      }, PHONE_INPUT_CHAR_MS);
    }

    return () => {
      timers.forEach((id) => window.clearTimeout(id));
      if (intervalId !== undefined) window.clearInterval(intervalId);
    };
  }, [visibleCount, scenario.bubbles, scenarioIdx, paused, fading]);

  /* cycle scenario after conversation ends */
  useEffect(() => {
    if (!finished || paused || fading) return;

    const t = window.setTimeout(() => {
      setFading(true);
      setPaused(true);
      setInputDraft("");
      setIsComposing(false);
      setSendPulse(false);

      window.setTimeout(() => {
        setScenarioIdx((i) => (i + 1) % SCENARIOS.length);
        setVisibleCount(0);
        setIsBotTyping(false);
        setFading(false);
        setPaused(false);
      }, PHONE_FADE_MS);
    }, PHONE_PAUSE_AFTER_SCENARIO_MS);

    return () => window.clearTimeout(t);
  }, [finished, paused, fading]);

  const selectScenario = (idx: number) => {
    if (idx === scenarioIdx) return;
    setFading(true);
    setPaused(true);
    setInputDraft("");
    setIsComposing(false);
    setSendPulse(false);

    window.setTimeout(() => {
      setScenarioIdx(idx);
      setVisibleCount(0);
      setIsBotTyping(false);
      setFading(false);
      setPaused(false);
    }, PHONE_FADE_MS);
  };

  return (
    <div className="relative flex flex-col justify-center items-center">
      {/* Ambient glow behind phone */}
      <div className="absolute inset-0 bg-brand-100/40 rounded-full blur-3xl scale-90 pointer-events-none" />

      {/* ── Phone frame — fixed 290 × 620 (≈ iPhone proportions) ── */}
      <div
        className="relative"
        style={{ width: 290 }}
      >
        {/* Outer shell */}
        <div
          className="relative bg-gray-900 rounded-[2.8rem] overflow-hidden"
          style={{
            width: 290,
            height: 620,
            boxShadow:
              "0 40px 80px rgba(0,0,0,0.35), 0 0 0 1px rgba(255,255,255,0.06) inset, 0 0 0 3px #374151",
          }}
        >
          {/* Dynamic island */}
          <div className="absolute top-3 left-1/2 -translate-x-1/2 w-[90px] h-[26px] bg-black rounded-full z-20" />

          {/* Screen — sits 2 px inside the outer shell */}
          <div
            className="absolute inset-[2px] bg-gray-50 rounded-[2.65rem] overflow-hidden flex flex-col"
          >
            {/* Status bar */}
            <div className="shrink-0 flex items-center justify-between px-5 pt-5 pb-1">
              <span className="text-gray-800 text-[11px] font-semibold">9:41</span>
              {/* dynamic island spacer */}
              <div className="w-[90px]" />
              <div className="flex items-center gap-1.5">
                {/* wifi */}
                <svg className="w-3.5 h-3.5 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M1.5 8.5C5.35 4.65 10.37 2.5 12 2.5s6.65 2.15 10.5 6l-1.5 1.5c-3.45-3.45-7.5-5-9-5s-5.55 1.55-9 5L1.5 8.5zm3 3C7.5 8.5 9.75 7 12 7s4.5 1.5 7.5 4.5l-1.5 1.5C15.75 10.75 13.75 9.5 12 9.5s-3.75 1.25-6 3.5L4.5 11.5zm3 3c1.5-1.5 2.75-2 4.5-2s3 .5 4.5 2l-1.5 1.5C14 15 13 14.5 12 14.5s-2 .5-3 1.5L7.5 14.5z" />
                </svg>
                {/* battery */}
                <svg className="w-5 h-3 text-gray-600" viewBox="0 0 24 12" fill="none">
                  <rect x="0.75" y="0.75" width="20.5" height="10.5" rx="2.25" stroke="currentColor" strokeWidth="1.5" />
                  <rect x="21.5" y="3.5" width="2" height="5" rx="1" fill="currentColor" opacity="0.5" />
                  <rect x="2" y="2" width="15" height="8" rx="1.5" fill="currentColor" />
                </svg>
              </div>
            </div>

            {/* Chat app header */}
            <div className="shrink-0 bg-brand-500 px-4 py-2.5 flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-white/20 flex items-center justify-center font-bold text-white text-sm shrink-0">
                C
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold text-[13px] leading-tight">Cravbot</p>
                <p className="text-orange-200 text-[10px]">AI Ordering • online</p>
              </div>
              <svg className="w-4 h-4 text-white/50 shrink-0" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <circle cx="12" cy="12" r="1" /><circle cx="19" cy="12" r="1" /><circle cx="5" cy="12" r="1" />
              </svg>
            </div>

            {/* Scenario chip */}
            <div className="shrink-0 bg-brand-50 border-b border-brand-100 px-4 py-1.5 flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-brand-400 animate-pulse" />
              <span className="text-brand-600 text-[10px] font-semibold truncate">{scenario.label}</span>
            </div>

            {/* Messages — scrollable, fixed height fills remaining space */}
            <div
              ref={chatContainerRef}
              className={`flex-1 overflow-y-auto px-3 py-3 space-y-2 bg-gray-50 transition-opacity duration-500 ${fading ? "opacity-0" : "opacity-100"}`}
              style={{ scrollbarWidth: "none" }}
            >
              <AnimatePresence initial={false}>
                {scenario.bubbles.slice(0, visibleCount).map((bubble, i) => (
                  <motion.div
                    key={`${scenarioIdx}-${i}`}
                    initial={{ opacity: 0, y: 6, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.28 }}
                    className={`flex ${bubble.from === "customer" ? "justify-end" : "justify-start"}`}
                  >
                    <div
                      className={`max-w-[80%] px-3 py-2 text-[11px] leading-relaxed ${
                        bubble.from === "customer"
                          ? "bg-brand-500 text-white rounded-2xl rounded-br-sm"
                          : "bg-white text-gray-800 rounded-2xl rounded-bl-sm shadow-sm border border-gray-100"
                      }`}
                    >
                      {bubble.text}
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>

              {/* Typing dots — bot is composing */}
              {!fading && isBotTyping && visibleCount < scenario.bubbles.length && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex justify-start">
                  <div className="bg-white border border-gray-100 rounded-2xl rounded-bl-sm px-3 py-2 shadow-sm flex gap-1 items-center">
                    {[0, 1, 2].map((i) => (
                      <span
                        key={i}
                        className="w-1.5 h-1.5 bg-gray-300 rounded-full animate-bounce"
                        style={{ animationDelay: `${i * 0.14}s` }}
                      />
                    ))}
                  </div>
                </motion.div>
              )}
              <div ref={bottomRef} />
            </div>

            {/* Input bar */}
            <div className="shrink-0 bg-white border-t border-gray-100 px-3 py-2.5 flex items-center gap-2">
              <div
                className={`flex-1 rounded-full px-3 py-1.5 text-[10px] min-h-[28px] flex items-center transition-colors duration-200 ${
                  isBotTyping
                    ? "bg-gray-100"
                    : inputDraft
                      ? "bg-white border border-gray-200"
                      : "bg-gray-100"
                }`}
              >
                {isBotTyping ? (
                  <span className="text-gray-400 truncate select-none">Assistant is typing…</span>
                ) : inputDraft || isComposing ? (
                  <span className="text-gray-800 truncate select-none">
                    {inputDraft}
                    {isComposing && (
                      <span className="inline-block w-px h-[1em] bg-brand-500 ml-px align-[-0.05em] animate-pulse" />
                    )}
                  </span>
                ) : (
                  <span className="text-gray-400 select-none">Type a message…</span>
                )}
              </div>
              <motion.div
                animate={
                  sendPulse
                    ? { scale: 0.86, backgroundColor: "#ea580c" }
                    : { scale: 1, backgroundColor: inputDraft ? "#f97316" : "#fdba74" }
                }
                transition={{ type: "spring", stiffness: 520, damping: 22 }}
                className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
              >
                <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
              </motion.div>
            </div>

            {/* Home indicator */}
            <div className="shrink-0 flex justify-center py-1.5 bg-white">
              <div className="w-24 h-1 bg-gray-300 rounded-full" />
            </div>
          </div>
        </div>

        {/* Physical side buttons */}
        <div className="absolute right-[-5px] top-28 w-[4px] h-12 bg-gray-600 rounded-r-full" />
        <div className="absolute left-[-5px] top-24 w-[4px] h-8 bg-gray-600 rounded-l-full" />
        <div className="absolute left-[-5px] top-36 w-[4px] h-8 bg-gray-600 rounded-l-full" />
        <div className="absolute left-[-5px] top-48 w-[4px] h-8 bg-gray-600 rounded-l-full" />
      </div>

      {/* Scenario indicator dots */}
      <div className="mt-6 flex gap-2 z-10">
        {SCENARIOS.map((s, i) => (
          <button
            key={s.id}
            aria-label={`Switch to ${s.label}`}
            onClick={() => selectScenario(i)}
            className={`rounded-full transition-all duration-300 ${
              i === scenarioIdx ? "w-7 h-2 bg-brand-500" : "w-2 h-2 bg-gray-300 hover:bg-brand-300"
            }`}
          />
        ))}
      </div>
    </div>
  );
}

/* ── stat card with count-up ─────────────────────────────────────────── */

function StatCard({ value, suffix, label, prefix }: {
  value: number;
  suffix?: string;
  prefix?: string;
  label: string;
}) {
  const { ref, count } = useCountUp(value, 1600);
  return (
    <div ref={ref} className="text-center px-4">
                <p className="text-4xl md:text-5xl font-extrabold gradient-text mb-1 tabular-nums leading-tight">
        {prefix}{count.toLocaleString()}{suffix}
      </p>
              <p className="text-gray-500 text-sm font-medium">{label}</p>
    </div>
  );
}

/* ── step card with large visible number ────────────────────────────── */

function StepCard({
  num,
  icon,
  title,
  desc,
  iconBg,
  delay,
}: {
  num: number;
  icon: React.ReactNode;
  title: string;
  desc: string;
  iconBg: string;
  delay: number;
}) {
  const { ref, count } = useCountUp(num, 900);

  return (
    <Reveal delay={delay}>
      <div ref={ref} className="bg-white border border-gray-100 shadow-sm rounded-2xl p-7 h-full min-h-[280px] hover:shadow-md hover:border-brand-100 transition-all duration-300 group">
        <div className="flex items-start justify-between mb-5">
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${iconBg} group-hover:scale-110 transition-transform duration-300`}>
            {icon}
          </div>
          <span className="text-6xl font-black tabular-nums leading-none"
            style={{ background: "linear-gradient(135deg, #fed7aa 0%, #f97316 100%)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
            {String(count).padStart(2, "0")}
          </span>
        </div>
        <h3 className="text-gray-900 font-bold text-lg mb-2">{title}</h3>
        <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
        <div className="mt-4 h-0.5 w-0 bg-brand-400 group-hover:w-full transition-all duration-500 rounded-full" />
      </div>
    </Reveal>
  );
}

/* ── feature card ─────────────────────────────────────────────────────── */

function FeatureCard({ icon, title, desc, iconBg }: {
  icon: React.ReactNode;
  title: string;
  desc: string;
  iconBg: string;
}) {
  return (
    <div className="feature-card rounded-2xl p-6 cursor-default group h-full flex flex-col">
      <div className={`w-11 h-11 rounded-xl flex items-center justify-center mb-4 ${iconBg} group-hover:scale-110 transition-transform duration-300 shrink-0`}>
        {icon}
      </div>
        <h3 className="text-gray-900 font-semibold text-base mb-2">{title}</h3>
        <p className="text-gray-500 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}

/* ── plan preview card ─────────────────────────────────────────────────── */

function PlanCard({ plan, highlight }: { plan: SubscriptionPlanCatalogEntry; highlight?: boolean }) {
  const features = landingPlanBullets(plan);
  const { online, dineIn } = plan.limits;

  return (
    <div className={`relative h-full rounded-2xl p-6 flex flex-col transition-all duration-300 ${
        highlight
          ? "bg-brand-500 text-white shadow-lg shadow-brand-500/20 hover:shadow-xl hover:shadow-brand-500/30"
          : "bg-white border border-gray-100 shadow-sm hover:shadow-md hover:border-brand-200"
    }`}>
      <div className="h-6 flex items-center justify-center shrink-0 mb-2">
        {highlight ? (
          <span className="bg-gray-900 text-white text-xs font-bold px-3 py-1 rounded-full">Most Popular</span>
        ) : null}
      </div>

      <div>
        <span className={`text-xs font-semibold uppercase tracking-widest ${highlight ? "text-orange-200" : "text-brand-500"}`}>{plan.tierLabel}</span>
        <h3 className={`text-2xl font-extrabold mt-1 ${highlight ? "text-white" : "text-gray-900"}`}>{plan.name}</h3>
        <p className={`text-sm mt-2 min-h-[3.25rem] ${highlight ? "text-orange-100" : "text-gray-500"}`}>
          <span className={`font-semibold ${highlight ? "text-white" : "text-gray-800"}`}>
            {online.freeOrdersIncluded.toLocaleString()}
          </span>{" "}
          online orders/month
          {dineIn.freeOrdersIncluded > 0 ? (
            <>
              {" · "}
              <span className={`font-semibold ${highlight ? "text-white" : "text-gray-800"}`}>
                {dineIn.freeOrdersIncluded.toLocaleString()}
              </span>{" "}
              dine-in
            </>
          ) : (
            <span className={highlight ? "text-orange-200/80" : "text-gray-400"}> · no dine-in</span>
          )}
        </p>
      </div>

      <ul className="space-y-2 flex-1 mt-5 mb-5">
        {features.map((f) => (
          <li key={f} className={`flex items-start gap-2 text-sm ${highlight ? "text-orange-100" : "text-gray-600"}`}>
            <svg className={`w-4 h-4 shrink-0 mt-0.5 ${highlight ? "text-white" : "text-brand-500"}`} fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
            </svg>
            {f}
          </li>
        ))}
      </ul>

      <Link to="/contact" className={`mt-auto text-center py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 ${
        highlight ? "bg-white text-brand-600 hover:bg-orange-50" : "border border-gray-200 text-gray-700 hover:bg-gray-50"
      }`}>
        Get Started
      </Link>
    </div>
  );
}

/* ── page ─────────────────────────────────────────────────────────────── */

export function Landing() {
  return (
    <div className="min-h-screen bg-white text-gray-900 overflow-x-hidden transition-colors duration-200">
      <PublicNav />

      {/* ── Hero ─────────────────────────────────────────────────────── */}
      <section className="relative min-h-screen flex items-center overflow-hidden bg-gradient-to-b from-orange-50/70 via-white to-white">
        <div className="absolute inset-0 dot-grid opacity-70" />
        <div className="absolute top-1/4 right-[10%] w-[500px] h-[500px] bg-brand-100/30 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-1/3 left-[5%] w-72 h-72 bg-purple-100/20 rounded-full blur-3xl pointer-events-none" />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-x-20 lg:gap-y-6 items-start lg:[grid-template-rows:max-content_1fr]">
            <div className="text-center lg:text-left">
              <motion.div
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.45 }}
                className="inline-flex items-center gap-2 text-xs font-semibold text-brand-600 bg-brand-50 border border-brand-100 rounded-full px-4 py-1.5 mb-6"
              >
                <span className="w-1.5 h-1.5 rounded-full bg-brand-500 animate-pulse" />
                Powered by Google Gemini AI
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 28 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.1 }}
                className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-extrabold leading-[1.07] tracking-tight text-gray-900"
              >
                The AI Ordering{" "}
                <span className="gradient-text">Engine</span>{" "}
                <br className="hidden sm:block" />
                for Restaurants
              </motion.h1>
            </div>

            <motion.div
              initial={{ opacity: 0, y: 32, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.75, delay: 0.28 }}
              className="flex justify-center lg:col-start-2 lg:row-start-1 lg:row-span-2"
            >
              <div className="animate-float-slow">
                <PhoneMockup />
              </div>
            </motion.div>

            <div className="text-center lg:text-left">
              <motion.p
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.55, delay: 0.22 }}
                className="text-base sm:text-lg text-gray-500 leading-relaxed max-w-xl mx-auto lg:mx-0"
              >
                Integrate Cravbot's AI into your website, app, or any chat platform.
                Your customers get a seamless ordering experience — you manage everything
                from one powerful dashboard.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.36 }}
                className="mt-8 flex flex-wrap gap-3 justify-center lg:justify-start"
              >
                <Link to="/contact" className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 sm:py-3.5 bg-brand-500 hover:bg-brand-600 text-white font-semibold rounded-xl btn-glow text-sm transition-colors">
                  Get Started
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </Link>
                <Link to="/demo" className="inline-flex items-center gap-2 px-5 sm:px-6 py-3 sm:py-3.5 border border-brand-200 hover:border-brand-400 bg-brand-50/60 hover:bg-brand-50 text-brand-700 font-semibold rounded-xl text-sm transition-all">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" /><path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  See Demo
                </Link>
                <Link to="/plans" className="inline-flex items-center px-5 sm:px-6 py-3 sm:py-3.5 border border-gray-200 hover:border-gray-300 text-gray-600 hover:text-gray-900 font-semibold rounded-xl text-sm transition-all">
                  View Plans
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.65 }}
                className="mt-8 flex flex-wrap items-center gap-5 justify-center lg:justify-start text-xs text-gray-400"
              >
                {["Deploy on any platform", "Real-time dashboard", "No per-seat fees"].map((t) => (
                  <span key={t} className="flex items-center gap-1.5">
                    <svg className="w-3.5 h-3.5 text-brand-500 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    {t}
                  </span>
                ))}
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Stats ──────────────────────────────────────────────────────── */}
      <section className="py-16 border-y border-gray-100 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <StatCard value={maxOnlineOrdersIncluded()} suffix="+" label="Online Orders per Plan" />
            <StatCard value={99} suffix=".9%" label="Platform Uptime" />
            <StatCard value={3} label="Core Plans" />
            <StatCard value={24} suffix="/7" label="AI Availability" />
          </div>
        </div>
      </section>

      {/* ── How It Works ───────────────────────────────────────────────── */}
      <section className="py-24 sm:py-28 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <p className="text-brand-600 text-sm font-semibold tracking-widest uppercase mb-3">How It Works</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900">
              From integration to orders,{" "}
              <span className="gradient-text">in minutes</span>
            </h2>
            <p className="mt-4 text-gray-500 max-w-xl mx-auto text-sm sm:text-base">
              Connect the Cravbot API to your existing platform. Your customers start ordering
              immediately — no new apps, no friction.
            </p>
          </Reveal>

          {/* Connecting line (desktop only) */}
          <div className="hidden lg:block relative">
            <div className="absolute top-[52px] left-[calc(16.67%+24px)] right-[calc(16.67%+24px)] h-px">
              <div className="h-full bg-gradient-to-r from-transparent via-brand-300 to-transparent" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 items-stretch">
            <StepCard
              num={1}
              icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>}
              title="Integrate the API"
              desc="Connect Cravbot's ordering API to your website, mobile app, or any platform your customers already use."
              iconBg="bg-brand-50 text-brand-600"
              delay={0}
            />
            <StepCard
              num={2}
              icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>}
              title="AI Handles the Conversation"
              desc="Cravbot's AI takes the full order — menu browsing, customisations, upsells, and confirmation — automatically."
              iconBg="bg-purple-50 text-purple-600"
              delay={130}
            />
            <StepCard
              num={3}
              icon={<svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0H3" /></svg>}
              title="Manage on Your Dashboard"
              desc="Orders flow into your dashboard. Approve, track, update your menu and hours — all in real time."
              iconBg="bg-green-50 text-green-600"
              delay={260}
            />
          </div>
        </div>
      </section>

      {/* ── Features ───────────────────────────────────────────────────── */}
      <section className="py-24 sm:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <p className="text-purple-600 text-sm font-semibold tracking-widest uppercase mb-3">Platform Features</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900">
              Everything your restaurant{" "}
              <span className="gradient-text">needs</span>
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 items-stretch">
            {[
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>, title: "AI Ordering Bot", desc: "Google Gemini–powered AI understands natural language and handles complex orders on any platform you deploy it.", iconBg: "bg-brand-50 text-brand-600" },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>, title: "Real-Time Dashboard", desc: "Track revenue, order volume, and top items. Beautiful charts with 7-day, 30-day, and 90-day time filters.", iconBg: "bg-purple-50 text-purple-600" },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" /></svg>, title: "Dynamic Menu Management", desc: "Add, edit, or mark items out of stock instantly. The AI stops offering unavailable items immediately.", iconBg: "bg-cyan-50 text-cyan-600" },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, title: "Smart Operating Hours", desc: "Set hours per day, including overnight. The bot respects them automatically and handles after-hours customers gracefully.", iconBg: "bg-yellow-50 text-yellow-600" },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>, title: "Order Approval Flow", desc: "Review every order before it's confirmed. Approve or reject with one click from your dashboard.", iconBg: "bg-green-50 text-green-600" },
              { icon: <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={1.75} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>, title: "Multi-Staff Access", desc: "Onboard your team with owner, manager, and staff roles. Each person gets the right level of access.", iconBg: "bg-pink-50 text-pink-600" },
            ].map(({ icon, title, desc, iconBg }) => (
              <Reveal key={title} delay={50} className="h-full">
                <FeatureCard icon={icon} title={title} desc={desc} iconBg={iconBg} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── Plans Preview ──────────────────────────────────────────────── */}
      <section className="py-24 sm:py-28 bg-gray-50 relative overflow-hidden">
        <div className="absolute inset-0 dot-grid opacity-50" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal className="text-center mb-14">
            <p className="text-brand-600 text-sm font-semibold tracking-widest uppercase mb-3">Pricing</p>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-gray-900">
              Plans that grow{" "}
              <span className="gradient-text">with you</span>
            </h2>
            <p className="mt-4 text-gray-500 max-w-lg mx-auto text-sm sm:text-base">
              Every plan includes the full AI ordering engine, owner dashboard, and API access.
            </p>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto items-stretch">
            {PUBLIC_PRICING_PLANS.map((plan, i) => (
              <Reveal key={plan.planId} delay={i * 120} className="h-full">
                <PlanCard plan={plan} highlight={plan.highlight} />
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-8 max-w-5xl mx-auto" delay={360}>
            <GalaEnterpriseCallout />
          </Reveal>

          <Reveal className="text-center mt-8">
            <Link to="/plans" className="text-brand-600 hover:text-brand-500 text-sm font-medium transition-colors">
              Compare all plans in detail →
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ── CTA Banner ─────────────────────────────────────────────────── */}
      <section className="py-20 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal>
            <div className="relative rounded-3xl overflow-hidden bg-brand-500">
              <div className="absolute inset-0 dot-grid opacity-15" />
              <div className="absolute top-0 right-0 w-64 h-64 bg-brand-400/30 rounded-full blur-2xl -translate-y-1/2 translate-x-1/2" />
              <div className="relative px-6 sm:px-10 py-12 sm:py-14 text-center">
                <h2 className="text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-4">
                  Ready to transform your restaurant?
                </h2>
                <p className="text-orange-100 text-sm sm:text-base mb-8 max-w-md mx-auto">
                  Cravbot is available by invitation. Contact our team and we'll have you
                  up and running within 24 hours.
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/contact" className="px-7 py-3.5 bg-white text-brand-600 font-bold rounded-xl hover:bg-orange-50 transition-colors text-sm shadow-sm">
                    Contact Us to Get Started
                  </Link>
                  <Link to="/contact?purpose=demo" className="px-7 py-3.5 border border-white/30 text-white font-semibold rounded-xl hover:bg-white/10 transition-colors text-sm">
                    Book a Free Demo
                  </Link>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <PublicFooter />
    </div>
  );
}
